import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Plus, Trash2, Users } from "lucide-react";

interface List { id: string; name: string; description: string | null; created_at: string; contact_count: number }
interface Contact { id: string; email: string; firstName: string | null; lastName: string | null }
interface ListsResp { lists: List[]; total: number; totalForUser: number; maxLists: number }

const PAGE_SIZE = 10;

export default function ListsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  function changeSearch(v: string) { setSearch(v); setPage(0); }

  const { data, isLoading } = useQuery({
    queryKey: ["lists", search, page],
    queryFn: () => api<ListsResp>(`/api/lists?q=${encodeURIComponent(search)}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [picker, setPicker] = useState<List | null>(null);

  const addMut = useMutation({
    mutationFn: (b: { name: string; description: string }) => api("/api/lists", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lists"] }); setShowAdd(false); setNewName(""); setNewDesc(""); },
  });
  const delMut = useMutation({
    mutationFn: (id: string) => api(`/api/lists/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lists"] }),
  });

  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const usage = data?.totalForUser ?? 0;
  const cap = data?.maxLists ?? 30;
  const atCap = usage >= cap;
  const usageTone = usage >= cap ? "text-red-600" : usage >= cap * 0.8 ? "text-amber-600" : "text-gray-500";

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">Lists</h1>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${usageTone}`}>{usage} / {cap} lists used</span>
          <button onClick={() => setShowAdd(true)} disabled={atCap}
            title={atCap ? `List limit reached (${cap}). Delete an unused list to create a new one.` : undefined}
            className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus size={14} /> New list
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-6">Group contacts into lists. Campaigns send to one or more lists.</p>

      {showAdd && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <input placeholder="List name" value={newName} onChange={(e) => setNewName(e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-2" />
          <input placeholder="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />
          {addMut.error && <p className="text-sm text-red-600 mb-2">{((addMut.error as ApiError).message)}</p>}
          <button onClick={() => addMut.mutate({ name: newName, description: newDesc })} disabled={!newName || addMut.isPending}
            className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700 disabled:opacity-50">Create</button>
          <button onClick={() => { setShowAdd(false); addMut.reset(); }} className="ml-2 text-sm text-gray-500">Cancel</button>
        </div>
      )}

      <input type="search" placeholder="Search lists…" value={search} onChange={(e) => changeSearch(e.target.value)}
        className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:border-indigo-500" />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {(data?.lists ?? []).map((l) => (
          <div key={l.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{l.name}</h3>
                {l.description && <p className="text-sm text-gray-500 mt-1 truncate">{l.description}</p>}
              </div>
              <button onClick={() => { if (confirm(`Delete "${l.name}"?`)) delMut.mutate(l.id); }}
                className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
            <div className="flex items-center gap-2 mt-3 text-sm text-gray-600">
              <Users size={14} /> {l.contact_count} contact{l.contact_count === 1 ? "" : "s"}
              <button onClick={() => setPicker(l)} className="ml-auto text-indigo-600 text-xs hover:underline">Add contacts</button>
            </div>
          </div>
        ))}
        {!isLoading && data?.lists.length === 0 && (
          <p className="text-sm text-gray-400">{search ? `No lists match "${search}".` : "No lists yet."}</p>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-gray-500 mt-4">
          <div>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Prev</button>
            <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {picker && <ContactPicker list={picker} onClose={() => { setPicker(null); qc.invalidateQueries({ queryKey: ["lists"] }); }} />}
    </div>
  );
}

function ContactPicker({ list, onClose }: { list: List; onClose: () => void }) {
  // Fetch ALL contacts, paging through the API (limit is capped at 200
  // server-side). Without this loop, users with >200 contacts could
  // never see — let alone add — anyone past the first page.
  const { data, isLoading } = useQuery({
    queryKey: ["contacts", "all"],
    queryFn: async () => {
      const all: Contact[] = [];
      let offset = 0;
      for (;;) {
        const pageData = await api<{ contacts: Contact[]; total: number }>(`/api/contacts?limit=200&offset=${offset}`);
        all.push(...pageData.contacts);
        if (pageData.contacts.length === 0 || all.length >= pageData.total) break;
        offset += 200;
      }
      return { contacts: all };
    },
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  // Filter by email (incl. domain) or name. Typing a company domain like
  // "bosch.com" narrows to just that company's addresses so they can be
  // bulk-selected via "Select all".
  const q = query.trim().toLowerCase();
  const contacts = data?.contacts ?? [];
  const filtered = q
    ? contacts.filter((c) =>
        c.email.toLowerCase().includes(q) ||
        [c.firstName, c.lastName].filter(Boolean).join(" ").toLowerCase().includes(q))
    : contacts;
  const allShownSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  function toggleSelectAll() {
    const s = new Set(selected);
    if (allShownSelected) filtered.forEach((c) => s.delete(c.id));
    else filtered.forEach((c) => s.add(c.id));
    setSelected(s);
  }

  const addMut = useMutation({
    mutationFn: () => api(`/api/lists/${list.id}/contacts`, {
      method: "POST", body: JSON.stringify({ contactIds: Array.from(selected) }),
    }),
    onSuccess: onClose,
  });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Add contacts to "{list.name}"</h3>
          <p className="text-xs text-gray-500">{selected.size} selected</p>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <input type="search" autoFocus placeholder="Search by email, company domain, or name…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-indigo-500" />
          <button onClick={toggleSelectAll} disabled={filtered.length === 0}
            className="whitespace-nowrap text-xs text-indigo-600 hover:underline disabled:opacity-40 disabled:no-underline">
            {allShownSelected ? "Clear all" : `Select all ${filtered.length}`}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading && <p className="px-5 py-4 text-sm text-gray-400">Loading contacts…</p>}
          {filtered.map((c) => (
            <label key={c.id} className="flex items-center gap-2 px-5 py-2 hover:bg-gray-50 text-sm cursor-pointer">
              <input type="checkbox" checked={selected.has(c.id)} onChange={(e) => {
                const s = new Set(selected); e.target.checked ? s.add(c.id) : s.delete(c.id); setSelected(s);
              }} />
              <span className="font-mono text-xs">{c.email}</span>
              <span className="text-gray-400 text-xs ml-auto">{[c.firstName, c.lastName].filter(Boolean).join(" ")}</span>
            </label>
          ))}
          {!isLoading && filtered.length === 0 && (
            <p className="px-5 py-4 text-sm text-gray-400">{q ? `No contacts match "${query}".` : "No contacts yet."}</p>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
          <button onClick={() => addMut.mutate()} disabled={selected.size === 0 || addMut.isPending}
            className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
            {addMut.isPending ? "Adding…" : `Add ${selected.size}`}
          </button>
        </div>
      </div>
    </div>
  );
}
